import React, { useEffect, useState } from 'react'

import { useNodesState, useEdgesState, Edge } from '@xyflow/react'
import { isEqual } from 'lodash'

import Box from 'components_new/atoms/Box'

import { BOARD } from 'components_new/molecules/Node/accents'

import { Node } from 'types/transformation/types/nodes'

import { useSIMTableActions } from 'redux/actions/DataPlatform/tables/v4'

import { useSimTable, useSourcesFetched } from 'redux/hooks/SimTables'

import Board from './Board'
import ChatPanel from './ChatPanel'
import DiscardDialog from './DiscardDialog'
import EditToolbar from './EditToolbar'
import Loading from './Loading'
import TransferTableDialog from './TransferTableDialog'
import VersionHistory from './VersionHistory'

import { getInitialElements } from './utils'

import { useGridIsInvalid } from '../Node/hooks'

const Transformation = () => {
  const { tryPutSIMTable, tryGetVersion, tryTransferSIMTableToProduction }
    = useSIMTableActions()

  const [editMode, setEditMode] = useState(false)
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const [historyOpen, setHistoryOpen] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const [activeTimestamp, setActiveTimestamp] = useState<string | null>(null)
  const [chatOpen, setChatOpen] = useState(false)
  const [transferOpen, setTransferOpen] = useState(false)
  const [transferring, setTransferring] = useState(false)
  const [layoutRequest, setLayoutRequest] = useState(0)

  const table = useSimTable()
  const sourcesFetched = useSourcesFetched()

  // The custom ontology board is view-only until its dedicated edit story.
  const readOnly = table?.data_product_type === 'CUSTOM_ONTOLOGY'

  // update board when table changes, unless user is previewing a version
  useEffect(() => {
    if (activeTimestamp) return

    if (table) {
      const initial = getInitialElements(table.nodes)

      setNodes(initial.nodes)
      setEdges(initial.edges)
    } else {
      setNodes([])
      setEdges([])
    }
  }, [table, activeTimestamp])

  const handleSelectVersion = (timestamp: string) => {
    if (!table) return

    tryGetVersion(table.id, timestamp).then((versionTable) => {
      if (!versionTable) return

      const initial = getInitialElements(versionTable.nodes)

      setActiveTimestamp(timestamp)
      setNodes(initial.nodes)
      setEdges(initial.edges)

      // Previewing a version normally drops into edit mode; keep it view-only
      // when the board is read-only (custom ontology).
      if (!readOnly) {
        setEditMode(true)
      }
    })
  }

  const handleCancelVersion = () => {
    setActiveTimestamp(null)

    if (table) {
      const initial = getInitialElements(table.nodes)

      setNodes(initial.nodes)
      setEdges(initial.edges)
    }
  }

  const disableSaveButton = useGridIsInvalid(nodes)

  const unlockEditMode = () => {
    setEditMode(false)
  }

  // Leave edit mode and reload the saved table (discarding unsaved edits).
  const exitEditMode = () => {
    unlockEditMode()
    handleCancelVersion()
  }

  const enterEditMode = () => {
    setEditMode(true)
  }

  // Compare the current board against the saved table (node data + positions,
  // order-independent). Connections live in node.data (left_node/right_node),
  // so this also covers edge changes.
  const hasUnsavedChanges = () => {
    if (!table) return false

    const normalize = (list: Node['data'][]) =>
      [...list].sort((a, b) => a.id.localeCompare(b.id))

    const current = normalize(
      nodes.map((node) => ({
        ...node.data,
        location_x: node.position.x,
        location_y: node.position.y
      }))
    )

    return !isEqual(current, normalize(table.nodes))
  }

  const handleToggleEdit = (nextEditMode: boolean) => {
    if (nextEditMode) {
      enterEditMode()

      return
    }

    // leaving edit mode with unsaved edits discards them → confirm first
    if (!hasUnsavedChanges()) {
      exitEditMode()

      return
    }

    setConfirmDiscard(true)
  }

  // The board serialized to the backend node format (same shape as
  // table.nodes) — used for saving and as chat context.
  const serializeBoard = () =>
    nodes.map((node) => ({
      ...node.data,
      location_x: node.position.x,
      location_y: node.position.y
    }))

  const handleSave = () => {
    if (!table) return

    tryPutSIMTable(table.id, serializeBoard(), (err?: string) => {
      if (!err) setActiveTimestamp(null)
      unlockEditMode()
    })
  }

  // The promote copies the version saved in the test environment, so it must
  // not run while the board shows something else — unsaved edits or an older
  // version the user is previewing.
  const transferBlockedReason = (() => {
    if (activeTimestamp) {
      return 'Visa den aktuella versionen för att kunna överföra'
    }

    if (hasUnsavedChanges()) {
      return 'Spara ändringarna innan du överför till produktion'
    }

    return undefined
  })()

  const handleTransfer = () => {
    if (!table) return

    setTransferring(true)

    // The dialog stays open on failure so the promote can be retried — the
    // snackbar has already said it went wrong.
    tryTransferSIMTableToProduction(table.id, (err?: string) => {
      setTransferring(false)

      if (!err) setTransferOpen(false)
    })
  }

  // A transformation proposed by the AI chat replaces the board as unsaved
  // changes, laid out by the board rather than at the positions the model
  // picked — the user reviews and persists it with the ordinary save flow.
  const handleProposal = (proposedNodes: Node['data'][]) => {
    const initial = getInitialElements(proposedNodes)

    setNodes(initial.nodes)
    setEdges(initial.edges)
    setLayoutRequest((request) => request + 1)

    if (!editMode) {
      enterEditMode()
    }
  }

  if (!table || !sourcesFetched) {
    return <Loading />
  }

  return (
    <>
      <Box
        sx={{
          bgcolor: BOARD.canvas,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <EditToolbar
          readOnly={readOnly}
          editMode={editMode}
          onToggleEdit={handleToggleEdit}
          onOpenVersions={() => setHistoryOpen(true)}
          onToggleChat={() => setChatOpen((open) => !open)}
          onSave={handleSave}
          saveDisabled={disableSaveButton}
          onTransfer={() => setTransferOpen(true)}
          transferBlockedReason={transferBlockedReason}
        />

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <Box sx={{ flex: 1 }}>
            <Board
              editMode={editMode}
              layoutRequest={layoutRequest}
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              setEdges={setEdges}
              setNodes={setNodes}
              table={table}
            />
          </Box>

          {chatOpen && !readOnly && (
            <ChatPanel
              table={table}
              onClose={() => setChatOpen(false)}
              getBoardNodes={serializeBoard}
              onProposal={handleProposal}
            />
          )}
        </Box>
      </Box>

      <VersionHistory
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        tableId={table.id}
        onSelectVersion={handleSelectVersion}
        activeTimestamp={activeTimestamp}
      />

      <TransferTableDialog
        open={transferOpen}
        loading={transferring}
        tableName={table.name}
        onClose={() => setTransferOpen(false)}
        onConfirm={handleTransfer}
      />

      <DiscardDialog
        open={confirmDiscard}
        onCancel={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false)
          exitEditMode()
        }}
      />
    </>
  )
}

export default Transformation
